import React, { useState, useEffect } from 'react';
import { 
    connectPostman,
    getPostmanWorkspaces,
    getPostmanCollections,
    syncPostmanCollection,
    getPostmanEnvironments
} from '../services/api';

export default function PostmanConnect({ onCollectionSynced, isConnected: initialConnected }) {
    const [connected, setConnected] = useState(initialConnected || false);
    const [apiKey, setApiKey] = useState('');
    const [workspaces, setWorkspaces] = useState([]);
    const [collections, setCollections] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('collections');
    const [syncingStatus, setSyncingStatus] = useState({}); // Track sync status per collection

    

    useEffect(() => {
        if (connected) {
            loadWorkspaces();
        }
    }, [connected]);

    useEffect(() => {
        if (selectedWorkspace) {
            loadCollections(selectedWorkspace === 'all' ? null : selectedWorkspace);
        }
    }, [selectedWorkspace]);

    const loadWorkspaces = async () => {
        try {
            const response = await getPostmanWorkspaces();
            setWorkspaces(response.data.workspaces || []);
            // Set default workspace to 'all'
            setSelectedWorkspace('all');
        } catch (err) {
            console.error('Error loading workspaces:', err);
        }
    };

    const handleConnect = async () => {
        try {
            setLoading(true);
            const response = await connectPostman(apiKey);
            setConnected(true);
            setWorkspaces(response.data.workspaces || []);
            setSelectedWorkspace('all');
            alert('Connected to Postman successfully!');
            
            // Load collections after connection
            await loadCollections();
        } catch (err) {
            alert('Failed to connect: ' + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    const loadCollections = async (workspaceId = null) => {
        try {
            setLoading(true);
            const response = await getPostmanCollections(workspaceId);
            setCollections(response.data.collections || []);
        } catch (err) {
            console.error('Error loading collections:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadEnvironments = async () => {
        try {
            const response = await getPostmanEnvironments();
            setEnvironments(response.data.environments || []);
        } catch (err) {
            console.error('Error loading environments:', err);
        }
    };

    const handleSyncCollection = async (collectionUid, collectionName) => {
        try {
            setSyncingStatus(prev => ({ ...prev, [collectionUid]: true }));
            const response = await syncPostmanCollection(collectionUid);
            alert(`Successfully synced: ${collectionName}`);
            if (onCollectionSynced) {
                onCollectionSynced();
            }
        } catch (err) {
            console.error('Sync error:', err);
            alert('Failed to sync collection: ' + (err.response?.data?.detail || err.message));
        } finally {
            setSyncingStatus(prev => ({ ...prev, [collectionUid]: false }));
        }
    };

    if (!connected) {
        return (
            <div className="max-w-2xl mx-auto p-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Connect to Postman</h3>
                    
                    <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">
                            To connect your Postman account, you'll need an API key.
                        </p>
                        <p className="text-sm text-gray-600">
                            Get your API key from: 
                            <a 
                                href="https://postman.com/settings/me/api-keys" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline ml-1"
                            >
                                Postman Settings → API Keys
                            </a>
                        </p>
                    </div>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Postman API Key
                            </label>
                            <input
                                type="password"
                                placeholder="PMAK-..."
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                            />
                        </div>
                        
                        <button
                            onClick={handleConnect}
                            disabled={!apiKey || loading}
                            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {loading ? 'Connecting...' : 'Connect to Postman'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Postman Integration</h3>
                    <span className="text-sm text-green-600">✓ Connected</span>
                </div>
                
                {/* Workspace Selector */}
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Select Workspace</label>
                    <select
                        value={selectedWorkspace || 'all'}
                        onChange={(e) => setSelectedWorkspace(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                    >
                        <option value="all">All Workspaces</option>
                        {workspaces.map(ws => (
                            <option key={ws.id} value={ws.id}>
                                {ws.name} ({ws.type})
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('collections')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'collections'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500'
                            }`}
                        >
                            Collections ({collections.length})
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('environments');
                                loadEnvironments();
                            }}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'environments'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500'
                            }`}
                        >
                            Environments
                        </button>
                    </nav>
                </div>
            </div>
            
            {/* Collections Tab */}
            {activeTab === 'collections' && (
                <div>
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-2">Loading collections...</p>
                        </div>
                    ) : collections.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No collections found in this workspace
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {collections.map(collection => (
                                <div key={collection.uid} className="bg-white rounded-lg shadow p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h4 className="font-medium">{collection.name}</h4>
                                            <p className="text-sm text-gray-500 mt-1">
                                                ID: {collection.uid}
                                            </p>
                                            {collection.owner && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Owner: {collection.owner}
                                                </p>
                                            )}
                                            {collection.isPublic && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
                                                    Public
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleSyncCollection(collection.uid, collection.name)}
                                            disabled={syncingStatus[collection.uid]}
                                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400"
                                        >
                                            {syncingStatus[collection.uid] ? 'Syncing...' : 'Sync'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Environments Tab */}
            {activeTab === 'environments' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {environments.map(env => (
                        <div key={env.uid} className="bg-white rounded-lg shadow p-4">
                            <h4 className="font-medium">{env.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">
                                ID: {env.uid}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}